import clsx from "clsx";
import type * as React from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import styles from "./input-group.module.scss";

type AlignValue = "inline-start" | "inline-end" | "block-start" | "block-end";

function InputGroup({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="input-group"
      role="group"
      className={clsx(styles.inputGroup, className)}
      {...props}
    />
  );
}

function InputGroupAddon({
  className,
  align = "inline-start",
  ...props
}: React.ComponentProps<"div"> & { align?: AlignValue }) {
  const alignClass = {
    "inline-start": styles.addonInlineStart,
    "inline-end": styles.addonInlineEnd,
    "block-start": styles.addonBlockStart,
    "block-end": styles.addonBlockEnd,
  }[align];

  return (
    <div
      role="group"
      data-slot="input-group-addon"
      data-align={align}
      className={clsx(styles.addon, alignClass, className)}
      onClick={(e) => {
        if ((e.target as HTMLElement).closest("button")) {
          return;
        }
        e.currentTarget.parentElement?.querySelector("input")?.focus();
      }}
      {...props}
    />
  );
}

type ButtonSize = "xs" | "sm" | "icon-xs" | "icon-sm";

function InputGroupButton({
  className,
  type = "button",
  variant = "ghost",
  size = "xs",
  ...props
}: Omit<React.ComponentProps<typeof Button>, "size"> & {
  size?: ButtonSize;
}) {
  const sizeClass = {
    xs: styles.buttonXs,
    sm: styles.buttonSm,
    "icon-xs": styles.buttonIconXs,
    "icon-sm": styles.buttonIconSm,
  }[size];

  return (
    <Button
      type={type}
      data-size={size}
      variant={variant}
      className={clsx(styles.groupButton, sizeClass, className)}
      {...props}
    />
  );
}

function InputGroupText({ className, ...props }: React.ComponentProps<"span">) {
  return <span className={clsx(styles.groupText, className)} {...props} />;
}

function InputGroupInput({
  className,
  ...props
}: React.ComponentProps<"input">) {
  return (
    <Input
      data-slot="input-group-control"
      className={clsx(styles.groupInput, className)}
      {...props}
    />
  );
}

function InputGroupTextarea({
  className,
  ...props
}: React.ComponentProps<"textarea">) {
  return (
    <Textarea
      data-slot="input-group-control"
      className={clsx(styles.groupTextarea, className)}
      {...props}
    />
  );
}

export {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupText,
  InputGroupInput,
  InputGroupTextarea,
};
